import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Paper,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { COLORS } from '../../../../../constants/radar';
import EmptyState from '../../../../common/EmptyState';
import LoadingSpinner from '../../../../common/LoadingSpinner';

const EditableItem = ({ 
  item, 
  index, 
  expanded, 
  setExpanded, 
  onChange, 
  onDelete,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  renderSummary,
  renderEditForm,
  disabled,
  singleSelection,
  readonly
}) => {
  const toggleExpanded = () => {
    if (!disabled && !singleSelection) {
      setExpanded(expanded === index ? null : index);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: '4px',
        mb: 2,
        overflow: 'hidden',
        opacity: disabled || readonly ? 0.7 : 1
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2,
          cursor: disabled || singleSelection ? 'default' : 'pointer',
          backgroundColor: expanded === index ? '#f5f7fa' : 'white',
          borderBottom: expanded === index ? '1px solid #e0e0e0' : 'none'
        }}
        onClick={toggleExpanded}
      >
        {!singleSelection && <DragIndicatorIcon sx={{ color: '#aaa', mr: 1 }} />}
        
        <Box sx={{ flex: 1 }}>
          {renderSummary(item)}
        </Box>
        
        {!singleSelection && !readonly && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onDelete(index);
              }}
              sx={{ mr: 1 }}
              disabled={disabled}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            
            {expanded === index ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </Box>
        )}
      </Box>
      
      {/* Expanded content */}
      {(expanded === index || singleSelection) && (
        <Box sx={{ p: 2 }}>
          {renderEditForm(item, (updatedItem) => onChange(index, updatedItem))}
          
          {!singleSelection && !readonly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button 
                disabled={isFirst || disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(index);
                }}
                size="small"
                sx={{ minWidth: 0, p: 0.5 }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </Button>
              <Button 
                disabled={isLast || disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(index);
                }}
                size="small"
                sx={{ minWidth: 0, p: 0.5, ml: 1 }}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

const EditableItemList = ({
  items,
  setItems,
  expandedIndex,
  setExpandedIndex,
  createNewItem,
  renderSummary,
  renderEditForm,
  addButtonText,
  emptyState = {
    title: "No items available",
    description: "Add one to get started."
  },
  itemName = "item",
  isLoading = false,
  disabled = false,
  disableAdd = false,
  singleSelection = false,
  customAction = null,
  readonly = false,
  onDelete = null
}) => {

  const handleAddItem = () => {
    const newItem = createNewItem(items);
    setItems([...items, newItem]);
    setExpandedIndex(items.length); // Expand the newly added item
  };

  const handleDeleteItem = (index) => {
    // If a custom onDelete handler is provided, use it; otherwise use default behavior
    if (onDelete) {
      onDelete(index);
    } else {
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
    }
  };
  
  const handleChangeItem = (index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    setItems(newItems);
  };
  
  const handleMoveUp = (index) => {
    if (index > 0) {
      const newItems = [...items];
      const temp = newItems[index];
      newItems[index] = newItems[index - 1];
      newItems[index - 1] = temp;
      setItems(newItems);
      
      // Keep the expanded item expanded
      if (expandedIndex === index) {
        setExpandedIndex(index - 1);
      } else if (expandedIndex === index - 1) {
        setExpandedIndex(index);
      }
    }
  };
  
  const handleMoveDown = (index) => {
    if (index < items.length - 1) {
      const newItems = [...items];
      const temp = newItems[index];
      newItems[index] = newItems[index + 1];
      newItems[index + 1] = temp;
      setItems(newItems);
      
      // Keep the expanded item expanded
      if (expandedIndex === index) {
        setExpandedIndex(index + 1);
      } else if (expandedIndex === index + 1) {
        setExpandedIndex(index);
      }
    }
  };

  return (
    <Box 
      autoComplete="off" 
      data-form-type="other"
      component="div"
    >
      {/* Hidden fake fields to trick the browser's autofill */}
      <div 
        aria-hidden="true" 
        style={{ 
          position: 'absolute', 
          top: '-999px', 
          left: '-999px', 
          height: 0, 
          width: 0, 
          opacity: 0
        }}
      >
        <input type="text" name="fakeusernameremembered" autoComplete="off" tabIndex="-1" />
        <input type="password" name="fakepasswordremembered" autoComplete="new-password" tabIndex="-1" />
      </div>
      
      {/* List content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner message={`Loading ${itemName}s...`} />
        </Box>
      ) : items.length > 0 ? (
        <>
          {!singleSelection && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                You've added {items.length} {items.length === 1 ? itemName : `${itemName}s`}. 
                Click any {itemName} to view or edit details.
              </Typography>
            </Box>
          )}
          {items.map((item, index) => (
            <EditableItem
              key={index}
              item={item}
              index={index}
              expanded={expandedIndex}
              setExpanded={setExpandedIndex}
              onChange={handleChangeItem}
              onDelete={handleDeleteItem}
              isFirst={index === 0}
              isLast={index === items.length - 1}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              renderSummary={renderSummary}
              renderEditForm={renderEditForm}
              disabled={disabled}
              singleSelection={singleSelection}
              readonly={readonly}
            />
          ))}
        </>
      ) : (
        <EmptyState 
          title={emptyState.title}
          description={emptyState.description}
          textSize='h8'
        />
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mt: 2
      }}>
        <Box>
          {!singleSelection && !readonly && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              disabled={isLoading || disabled || disableAdd}
              sx={{ 
                color: COLORS.PRIMARY,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {addButtonText}
            </Button>
          )}
        </Box>
        
        {customAction && !readonly && (
          <Box>
            {customAction}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EditableItemList; 